import { SyntheticEvent, useEffect, useRef } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { usePrivate } from '@/components/private';
import { useRouter } from 'next/navigation';
import { loginRequest } from '@/authConfig';
import { APP_ROUTE_PATHS, PUBLIC_ROUTES } from '@/configs/routes';
import { UserRoleName } from '@/lib/types';

export default function useAdminGuard(path: string) {
  const isAuthenticated = useIsAuthenticated();
  const { userOrganisations } = usePrivate();
  const { instance, inProgress } = useMsal();
  const router = useRouter();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isAdmin = userOrganisations?.some(
    (x) => x.roleNames?.some((r) => r === UserRoleName.Admin),
  );

  // after login, it can take some time for the user profile to be loaded
  function handleClickLink(e?: SyntheticEvent) {
    e?.preventDefault();

    if (inProgress !== 'none') {
      timeoutRef.current = setTimeout(handleClickLink, 100);
      return;
    }

    if (!isAuthenticated) {
      // set the redirect path to dashboard after login
      router.push(APP_ROUTE_PATHS.DASHBOARD);
      instance.loginRedirect(loginRequest);
      return;
    }

    if (!isAdmin) {
      router.push(PUBLIC_ROUTES.RESTRICTED);
      return;
    }

    router.push(path);
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  return {
    isAuthenticated,
    handleClickLink,
  };
}
